import { configClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import { toAuditEvent, toParameter, toParameterCategory } from '../mappers/configuration.mapper';
import type {
  AuditFiltersResponse,
  AuditLogResponse,
  CategoryResponse,
  PaginatedResponse,
  ParameterResponse,
  ParametersByCategoryResponse,
  ParameterUpdateRequest,
  PlatformResponse,
} from '../types/configuration.api.types';
import type {
  AuditEvent,
  AuditTrailUser,
  ConfigurationPlatform,
  Parameter,
  ParameterCategory,
  ParameterScope,
  ParameterValue,
} from '../types/configuration.types';

/** `page` discriminator required by `/config-audit-trail` — selects which
 *  config surface's log to read. Only `'parameters'` is used today. */
const CONFIG_AUDIT_TRAIL_PAGE = 'parameters';

/** Cap used by per-parameter history (single in-line list, no infinite scroll).
 *  Backend caps `limit` at 200 — we request the max so a parameter's full
 *  history fits in one call. The platform-wide audit trail is paginated
 *  separately via `fetchAuditTrail` and `useInfiniteQuery`. */
const AUDIT_PAGE_LIMIT = 200;

/** Default page size for the platform-wide audit trail. Picked to balance
 *  round-trip count against payload size (~50 rows × ~250B ≈ 12KB). */
const AUDIT_TRAIL_PAGE_SIZE = 50;

/** Header the agentic-config service expects for platform scoping. */
const PLATFORM_HEADER = 'X-Platform-ID';

// ---------------------------------------------------------------------------
// Platforms — resolves the human-readable name (e.g. 'GMC') to a UUID used in
// every other call's `X-Platform-ID` header.
// ---------------------------------------------------------------------------

/** Fetches the platform catalogue. Cached forever at the hook layer
 *  (`usePlatformId`) — platforms change at deploy cadence, not session. */
export async function fetchPlatforms(): Promise<PlatformResponse[]> {
  return configClient.get(endpoints.configuration.platforms()).json<PlatformResponse[]>();
}

// ---------------------------------------------------------------------------
// Parameters
// ---------------------------------------------------------------------------

export interface FetchParametersParams {
  /** Platform UUID — sent as the X-Platform-ID header. */
  platformId: string;
  /** Platform name — stamped onto each mapped Parameter. */
  platformName: ConfigurationPlatform;
}

/** Fetches the full parameter list for a platform. The backend returns the
 *  shape grouped by category (`ParametersByCategoryResponse[]`); we flatten
 *  here because the page re-groups against the parallel
 *  `/parameters/categories` query. Categories are resolved purely by UUID,
 *  so renaming a category on the backend never makes a parameter disappear. */
export async function fetchParameters({
  platformId,
  platformName,
}: FetchParametersParams): Promise<Parameter[]> {
  const groups = await configClient
    .get(endpoints.configuration.parameters(), {
      headers: { [PLATFORM_HEADER]: platformId },
    })
    .json<ParametersByCategoryResponse[]>();

  const out: Parameter[] = [];
  for (const group of groups) {
    for (const wire of group.parameters) out.push(toParameter(wire, platformName));
  }
  return out;
}

/** Fetches the subsidiary-resolved parameter list — same grouped-by-category
 *  shape as `fetchParameters`, but each parameter's `current_value` is the
 *  active subsidiary's override when set, else the inherited default
 *  (`is_override` distinguishes the two). `X-Subsidiary-ID` is attached
 *  automatically by `subsidiaryInterceptor`; only the platform header is
 *  set explicitly here, same as the global list. */
export async function fetchSubsidiaryParameters({
  platformId,
  platformName,
}: FetchParametersParams): Promise<Parameter[]> {
  const groups = await configClient
    .get(endpoints.configuration.subsidiaryParameters(), {
      headers: { [PLATFORM_HEADER]: platformId },
    })
    .json<ParametersByCategoryResponse[]>();

  const out: Parameter[] = [];
  for (const group of groups) {
    for (const wire of group.parameters) out.push(toParameter(wire, platformName));
  }
  return out;
}

/** Fetches the platform's category catalog (id, name, section, sort_order,
 *  parameter_count). Drives the left rail render order, section grouping,
 *  and per-category counts. Cached per `platformId`. */
export async function fetchParameterCategories(platformId: string): Promise<ParameterCategory[]> {
  const wire = await configClient
    .get(endpoints.configuration.parameterCategories(), {
      headers: { [PLATFORM_HEADER]: platformId },
    })
    .json<CategoryResponse[]>();
  return wire.map(toParameterCategory);
}

/** Per-parameter audit history — the History dialog always reads
 *  `/config-audit-trail?page=parameters` filtered by `definition_ids`,
 *  regardless of global/subsidiary scope. `/audit-trail`'s `parameter_ids`
 *  filter does NOT return subsidiary-scoped edits, so it can't be used here;
 *  `/config-audit-trail` is the only endpoint with the full per-parameter
 *  log in every mode. Callers must pass the parameter's `definition_id`
 *  value (`Parameter.definitionId`), not its row `id`, as the
 *  `definition_ids` query value — the backend matches history against the
 *  shared definition, and a row's own `id` returns nothing.
 *
 *  ASSUMPTION (flagged, unverified): the contract doc for this endpoint isn't
 *  available in this repo, so the response rows are assumed to share the
 *  existing `AuditLogResponse` shape (`parameter_id`, `user_name`,
 *  `previous_value`, `new_value`, `reason`, `created_at`) inside the same
 *  `PaginatedResponse` envelope. Verify against the real OpenAPI contract
 *  before relying on this in production — see docs/configuration-manager.md. */
export async function fetchParameterHistory(
  definitionId: string,
  platformId: string,
): Promise<AuditEvent[]> {
  const page = await configClient
    .get(endpoints.configuration.configAuditTrail(), {
      headers: { [PLATFORM_HEADER]: platformId },
      searchParams: {
        page: CONFIG_AUDIT_TRAIL_PAGE,
        definition_ids: definitionId,
        limit: AUDIT_PAGE_LIMIT,
        offset: 0,
      },
    })
    .json<PaginatedResponse<AuditLogResponse>>();

  return page.items.map(toAuditEvent);
}

// ---------------------------------------------------------------------------
// Audit Trail
// ---------------------------------------------------------------------------

export interface FetchAuditTrailParams {
  platformId: string;
  search?: string;
  parameterIds?: string[];
  /** Backend filters by user *name* (CSV) — frontend stores these in
   *  `AuditTrailFilters.userIds` for historical reasons; the value happens
   *  to be the user's display name. */
  userNames?: string[];
  /** Inclusive ISO bounds — backend accepts date-only (`YYYY-MM-DD`); we
   *  send ISO datetimes and the service truncates. */
  dateFrom?: string;
  dateTo?: string;
  /** Page size — defaults to AUDIT_TRAIL_PAGE_SIZE (50). */
  limit?: number;
  /** Offset (rows skipped) — defaults to 0. */
  offset?: number;
}

/** One page of the platform-wide audit trail. `total` is the unpaged row count
 *  for the current filter set — used by the FE to compute `hasNextPage` and
 *  render "Showing N of M". Items are newest-first (backend order). */
export interface AuditTrailPage {
  items: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
}

/** Fetches one page of the audit trail. Used by `useInfiniteQuery` —
 *  filters/search are sent as-is on every page so the backend re-counts and
 *  the FE only ever appends the latest slice. */
export async function fetchAuditTrail(params: FetchAuditTrailParams): Promise<AuditTrailPage> {
  const limit = params.limit ?? AUDIT_TRAIL_PAGE_SIZE;
  const offset = params.offset ?? 0;
  const searchParams: Record<string, string | number> = { limit, offset };
  if (params.search) searchParams.search = params.search;
  if (params.parameterIds && params.parameterIds.length > 0) {
    searchParams.parameter_ids = params.parameterIds.join(',');
  }
  if (params.userNames && params.userNames.length > 0) {
    searchParams.user_names = params.userNames.join(',');
  }
  // Send TZ-aware ISO datetimes verbatim. `customFrom` / `customTo` are
  // already produced by `startOfLocalDayISO` / `endOfLocalDayISO` (Luxon)
  // and carry the user's local offset, so the backend filter exactly
  // matches "all of [picked range] in my local TZ" — no UTC-day rounding.
  if (params.dateFrom) searchParams.start_datetime = params.dateFrom;
  if (params.dateTo) searchParams.end_datetime = params.dateTo;

  const page = await configClient
    .get(endpoints.configuration.auditTrail(), {
      headers: { [PLATFORM_HEADER]: params.platformId },
      searchParams,
    })
    .json<PaginatedResponse<AuditLogResponse>>();

  return {
    items: page.items.map(toAuditEvent),
    total: page.total,
    limit: page.limit,
    offset: page.offset,
  };
}

/** Subsidiary-scoped equivalent of `fetchAuditTrail`, for the Audit Trail tab
 *  when viewing a subsidiary. Reads `/config-audit-trail?page=parameters`
 *  filtered by `definition_ids` (mapped from `parameterIds`) instead of
 *  `/audit-trail`'s `parameter_ids`.
 *
 *  ASSUMPTION (flagged, unverified): same caveat as
 *  `fetchParameterHistory` — the response envelope and filter
 *  query-param names beyond `definition_ids` (e.g. `actions`, date bounds)
 *  are inferred from the existing `/audit-trail` contract, not confirmed
 *  against the real OpenAPI slice. */
export async function fetchSubsidiaryAuditTrail(
  params: FetchAuditTrailParams,
): Promise<AuditTrailPage> {
  const limit = params.limit ?? AUDIT_TRAIL_PAGE_SIZE;
  const offset = params.offset ?? 0;
  const searchParams: Record<string, string | number> = {
    page: CONFIG_AUDIT_TRAIL_PAGE,
    limit,
    offset,
  };
  if (params.search) searchParams.search = params.search;
  if (params.parameterIds && params.parameterIds.length > 0) {
    searchParams.definition_ids = params.parameterIds.join(',');
  }
  if (params.userNames && params.userNames.length > 0) {
    searchParams.user_names = params.userNames.join(',');
  }
  if (params.dateFrom) searchParams.start_datetime = params.dateFrom;
  if (params.dateTo) searchParams.end_datetime = params.dateTo;

  const page = await configClient
    .get(endpoints.configuration.configAuditTrail(), {
      headers: { [PLATFORM_HEADER]: params.platformId },
      searchParams,
    })
    .json<PaginatedResponse<AuditLogResponse>>();

  return {
    items: page.items.map(toAuditEvent),
    total: page.total,
    limit: page.limit,
    offset: page.offset,
  };
}

/** Filter options for the audit-trail UI: parameters that have been changed
 *  + users who made changes. Replaces the old `fetchAuditTrailUsers`. */
export interface AuditTrailFilterOptions {
  parameters: { id: string; label: string }[];
  users: AuditTrailUser[];
}

export async function fetchAuditTrailFilters(platformId: string): Promise<AuditTrailFilterOptions> {
  const res = await configClient
    .get(endpoints.configuration.auditTrailFilters(), {
      headers: { [PLATFORM_HEADER]: platformId },
    })
    .json<AuditFiltersResponse>();

  return {
    parameters: res.parameters.map((p) => ({ id: p.value, label: p.label })),
    // The backend's user `value` IS the display name (used in the filter
    // query), and `label` is the same string — frontend `AuditTrailUser.id`
    // therefore holds the name, matching the existing UI contract.
    users: res.users.map((u) => ({ id: u.value, name: u.label })),
  };
}

// ---------------------------------------------------------------------------
// Update parameter (PUT)
// ---------------------------------------------------------------------------

export interface UpdateParameterArgs {
  newValue: ParameterValue;
  scope: ParameterScope;
  reason: string;
  /** Acting user — required by the backend for the audit-log row. Injected at
   *  the hook layer from `auth.store`. */
  userId: string;
  userName: string;
  /** Platform context — used to map the response back to a `Parameter`. */
  platformName: ConfigurationPlatform;
}

/** Updates a parameter and returns the mapped result. */
export async function updateParameter(id: string, args: UpdateParameterArgs): Promise<Parameter> {
  const body: ParameterUpdateRequest = {
    new_value: String(args.newValue),
    scope: args.scope,
    reason: args.reason || null,
    user_id: args.userId,
    user_name: args.userName,
  };

  const wire = await configClient
    .put(endpoints.configuration.parameterById(id), { json: body })
    .json<ParameterResponse>();

  return toParameter(wire, args.platformName);
}

// ---------------------------------------------------------------------------
// Subsidiary parameters — set/clear an override for the active subsidiary
// ---------------------------------------------------------------------------

export interface UpdateSubsidiaryParameterArgs {
  newValue: ParameterValue;
  reason: string;
  userId: string;
  userName: string;
  platformName: ConfigurationPlatform;
}

/** Sets (or replaces) the active subsidiary's override for a parameter. No
 *  `scope` field — unlike the global PUT, a subsidiary override only ever
 *  applies to the subsidiary carried by `X-Subsidiary-ID`. */
export async function updateSubsidiaryParameter(
  id: string,
  args: UpdateSubsidiaryParameterArgs,
): Promise<Parameter> {
  const body: Omit<ParameterUpdateRequest, 'scope'> = {
    new_value: String(args.newValue),
    reason: args.reason || null,
    user_id: args.userId,
    user_name: args.userName,
  };

  const wire = await configClient
    .put(endpoints.configuration.subsidiaryParameterById(id), { json: body })
    .json<ParameterResponse>();

  return toParameter(wire, args.platformName);
}

export interface ClearSubsidiaryParameterOverrideArgs {
  userId: string;
  userName: string;
}

/** Clears the active subsidiary's override, reverting the parameter to the
 *  inherited default. `user_id`/`user_name` are sent as query params per the
 *  DELETE contract (no request body). Callers refetch the parameters list to
 *  pick up the reverted value rather than relying on a response body, since
 *  the DELETE response shape isn't guaranteed to carry the updated parameter. */
export async function clearSubsidiaryParameterOverride(
  id: string,
  args: ClearSubsidiaryParameterOverrideArgs,
): Promise<void> {
  await configClient.delete(endpoints.configuration.subsidiaryParameterById(id), {
    searchParams: { user_id: args.userId, user_name: args.userName },
  });
}
